import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Platform, View, StyleSheet, useWindowDimensions } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { createStackNavigator } from '@react-navigation/stack';
import { useTheme, ThemeProvider } from '@react-navigation/native';
import { isFullscreenAdminRoute, useAdminCurrentRoute } from './useAdminCurrentRoute';
import { useResponsive } from '@/hooks/common/useResponsive';
import { AdminSidebar } from '@/components/common/admin/desktop/AdminSidebar';
import { AdminHeaderWithMenu } from '@/components';
import { GradientBackground } from '@/components/shared/redesign';
import { useAppDispatch, useAppSelector } from '@/store';
import { selectIsSidebarCollapsed } from '@/store/selectors/adminUISelectors';
import { useAdminLogoutMutation } from '@/store/api';
import { resetAuth } from '@/store/slices/authSlice';
import { SecureStorage } from '@/services/shared/storage/SecureStorage';
import { ADMIN_PATH_TO_SCREEN, ADMIN_SCREEN_METADATA } from '@/utils/admin/navigation';
import { SEMANTIC_COLORS } from '@/constants/residents/residentColors';
import { IBulletinTableRow } from '@/types/admin/bulletins';
import { ICommunityResource } from '@/types/admin/resources';
import { IOrganization } from '@/types/admin/organizations';
import { IUserManagementTableRow } from '@/types/admin/userManagement';
import { IProperty } from '@/types/admin/properties';
import { ResidentList } from '@/screens/admin/resident/ResidentList';
import { ResidentDetails } from '@/screens/admin/resident/ResidentDetails';
import { TransactionHistoryScreen } from '@/screens/admin/resident/TransactionHistoryScreen';
import { EventsScreen } from '@/screens/admin/events/EventsScreen';
import { BulletinsScreen } from '@/screens/admin/bulletins/BulletinsScreen';
import { MessagesScreen } from '@/screens/admin/messages/MessagesScreen';
import { ResourcesScreen } from '@/screens/admin/resources/ResourcesScreen';
import { CreateResourceScreen } from '@/screens/admin/resources/CreateResourceScreen';
import { EditResourceScreen } from '@/screens/admin/resources/EditResourceScreen';
import { ToDosScreen } from '@/screens/admin/todoLists/ToDosScreen';
import { TodoListOccurrenceScreen } from '@/screens/admin/todoLists/TodoListOccurrenceScreen';
import { CreateTodoListScreen } from '@/screens/admin/todoLists/CreateTodoListScreen';
import { EditTodoListScreen } from '@/screens/admin/todoLists/EditTodoListScreen';
import { FundingScreen } from '@/screens/admin/funding/FundingScreen';
import { OrganizationsScreen } from '@/screens/admin/organizations/OrganizationsScreen';
import { CreateOrganizationScreen } from '@/screens/admin/organizations/CreateOrganizationScreen';
import { EditOrganizationScreen } from '@/screens/admin/organizations/EditOrganizationScreen';
import { UserManagementScreen } from '@/screens/admin/userManagement/UserManagementScreen';
import { PropertiesScreen } from '@/screens/admin/properties/PropertiesScreen';
import { CreatePropertyScreen } from '@/screens/admin/properties/CreatePropertyScreen';
import { EditPropertyScreen } from '@/screens/admin/properties/EditPropertyScreen';
import { FeatureFlagsScreen } from '@/screens/admin/featureFlags/FeatureFlagsScreen';
import { ChatScreen } from '@/screens/admin/messages/ChatScreen';
import { CreateEventScreen } from '@/screens/admin/events/CreateEventScreen';
import { EditEventScreen } from '@/screens/admin/events/EditEventScreen';
import { CreateBulletinScreen } from '@/screens/admin/bulletins/CreateBulletinScreen';
import { EditBulletinScreen } from '@/screens/admin/bulletins/EditBulletinScreen';
import { CreateUserScreen } from '@/screens/admin/userManagement/CreateUserScreen';
import { EditUserScreen } from '@/screens/admin/userManagement/EditUserScreen';
import { CreateMessageScreen } from '@/screens/admin/messages/CreateMessageScreen';
import { EditMessageScreen } from '@/screens/admin/messages/EditMessageScreen';
import { TagManagementScreen } from '@/screens/admin/corkBoard/TagManagementScreen';
import { ThreadModerationScreen } from '@/screens/admin/corkBoard/ThreadModerationScreen';
import { ReportReviewScreen } from '@/screens/admin/corkBoard/ReportReviewScreen';
import { SurveyResponsesScreen } from '@/screens/admin/corkBoard/SurveyResponsesScreen';
import { SurveysManagementScreen } from '@/screens/admin/corkBoard/SurveysManagementScreen';

export type AdminStackParamList = {
  ResidentList: undefined;
  ResidentDetails: {
    accountId: number;
    leaseId: number;
    mode?: 'account' | 'resident';
  };
  TransactionHistory: {
    accountId: number;
    leaseId: number;
    residentName?: string;
  };
  Events: undefined;
  CreateEvent: undefined;
  EditEvent: { eventId: number };
  Bulletins: undefined;
  CreateBulletin: undefined;
  EditBulletin: { bulletin: IBulletinTableRow };
  Messages: undefined;
  CreateMessage: undefined;
  EditMessage: { conversationId: number };
  Chat: { conversationId: number };
  Resources: undefined;
  CreateResource: { highestPosition?: number };
  EditResource: { resource: ICommunityResource };
  ToDos: undefined;
  TodoListOccurrence: {
    listId: number;
    /** One-shot post-save confirmation from the edit flow; cleared once shown. */
    savedBanner?: 'saved' | 'saved-applied';
  };
  CreateTodoList: undefined;
  EditTodoList: { listId: number };
  Funding: undefined;
  Organizations: undefined;
  CreateOrganization: undefined;
  EditOrganization: { organization: IOrganization };
  UserManagement: undefined;
  CreateUser: undefined;
  EditUser: { user: IUserManagementTableRow };
  Properties: undefined;
  CreateProperty: undefined;
  EditProperty: { property: IProperty };
  FeatureFlags: undefined;
  TagManagement: undefined;
  ThreadModeration: undefined;
  ReportReview: undefined;
  SurveyResponses: { threadId: string };
  SurveysManagement: undefined;
};

const Stack = createStackNavigator<AdminStackParamList>();

const SIDEBAR_EXPANDED_WIDTH = 280;
const SIDEBAR_COLLAPSED_WIDTH = 72;

export const AdminNavigator: React.FC = () => {
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const parentTheme = useTheme();

  const transparentTheme = useMemo(
    () => ({
      ...parentTheme,
      colors: { ...parentTheme.colors, background: 'transparent' },
    }),
    [parentTheme],
  );

  // From navigation state, not from transition/focus events: the shell header
  // below must toggle in the same commit as the stack swaps screens.
  const currentRoute = useAdminCurrentRoute();
  const adminNavRef = useRef<any>(null);

  const { height: windowHeight } = useWindowDimensions();
  const wrapperHeight =
    Platform.OS === 'web' ? { height: windowHeight } : undefined;

  const isSidebarCollapsed = useAppSelector(selectIsSidebarCollapsed);
  const sidebarWidth = useSharedValue(
    isSidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH,
  );
  useEffect(() => {
    sidebarWidth.value = withTiming(
      isSidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH,
      { duration: 200 },
    );
  }, [isSidebarCollapsed, sidebarWidth]);
  const sidebarAnimatedStyle = useAnimatedStyle(() => ({
    width: sidebarWidth.value,
  }));

  const dispatch = useAppDispatch();
  const [adminLogoutMutation] = useAdminLogoutMutation();

  const currentPath = useMemo(
    () => ADMIN_SCREEN_METADATA[currentRoute]?.path ?? '/properties',
    [currentRoute],
  );

  const handleNavigate = useCallback((path: string) => {
    const screenName = ADMIN_PATH_TO_SCREEN[path];
    if (screenName && adminNavRef.current) {
      adminNavRef.current.navigate(screenName);
    }
  }, []);

  const handleLogoPress = useCallback(() => {
    adminNavRef.current?.navigate('ResidentList');
  }, []);

  const handleSignOut = useCallback(async () => {
    try {
      await adminLogoutMutation().unwrap();
      await SecureStorage.getInstance().clearTokens();
      dispatch(resetAuth());
    } catch (error) {
      console.error('Admin sign out failed:', error);
    }
  }, [adminLogoutMutation, dispatch]);

  const navigator = (
    <Stack.Navigator
      initialRouteName="ResidentList"
      screenListeners={({ navigation }) => ({
        // Only to hold a navigator handle for the menu; the current route is
        // read from state (useAdminCurrentRoute), never from these events.
        transitionStart: (e: any) => {
          if (!e.data?.closing) adminNavRef.current = navigation;
        },
        focus: () => {
          adminNavRef.current = navigation;
        },
      })}
      screenOptions={{
        headerShown: false,
        animationEnabled: false,
        cardStyle: {
          backgroundColor: isDesktop ? SEMANTIC_COLORS.background.primary : 'transparent',
        },
      }}
    >
      <Stack.Screen name="ResidentList" component={ResidentList} />
      <Stack.Screen name="ResidentDetails" component={ResidentDetails} />
      <Stack.Screen
        name="TransactionHistory"
        component={TransactionHistoryScreen}
      />
      <Stack.Screen name="Events" component={EventsScreen} />
      <Stack.Screen name="CreateEvent" component={CreateEventScreen} />
      <Stack.Screen name="EditEvent" component={EditEventScreen} />
      <Stack.Screen name="Bulletins" component={BulletinsScreen} />
      <Stack.Screen name="CreateBulletin" component={CreateBulletinScreen} />
      <Stack.Screen name="EditBulletin" component={EditBulletinScreen} />
      <Stack.Screen name="Messages" component={MessagesScreen} />
      <Stack.Screen name="CreateMessage" component={CreateMessageScreen} />
      <Stack.Screen name="EditMessage" component={EditMessageScreen} />
      <Stack.Screen name="Chat" component={ChatScreen} />
      <Stack.Screen name="Resources" component={ResourcesScreen} />
      <Stack.Screen name="CreateResource" component={CreateResourceScreen} />
      <Stack.Screen name="EditResource" component={EditResourceScreen} />
      <Stack.Screen name="ToDos" component={ToDosScreen} />
      <Stack.Screen
        name="TodoListOccurrence"
        component={TodoListOccurrenceScreen}
      />
      <Stack.Screen name="CreateTodoList" component={CreateTodoListScreen} />
      <Stack.Screen name="EditTodoList" component={EditTodoListScreen} />
      <Stack.Screen name="Funding" component={FundingScreen} />
      <Stack.Screen name="Organizations" component={OrganizationsScreen} />
      <Stack.Screen
        name="CreateOrganization"
        component={CreateOrganizationScreen}
      />
      <Stack.Screen
        name="EditOrganization"
        component={EditOrganizationScreen}
      />
      <Stack.Screen name="UserManagement" component={UserManagementScreen} />
      <Stack.Screen name="CreateUser" component={CreateUserScreen} />
      <Stack.Screen name="EditUser" component={EditUserScreen} />
      <Stack.Screen name="Properties" component={PropertiesScreen} />
      <Stack.Screen name="CreateProperty" component={CreatePropertyScreen} />
      <Stack.Screen name="EditProperty" component={EditPropertyScreen} />
      <Stack.Screen name="FeatureFlags" component={FeatureFlagsScreen} />
      <Stack.Screen name="TagManagement" component={TagManagementScreen} />
      <Stack.Screen
        name="ThreadModeration"
        component={ThreadModerationScreen}
      />
      <Stack.Screen name="ReportReview" component={ReportReviewScreen} />
      <Stack.Screen name="SurveyResponses" component={SurveyResponsesScreen} />
      <Stack.Screen
        name="SurveysManagement"
        component={SurveysManagementScreen}
      />
    </Stack.Navigator>
  );

  if (isDesktop) {
    return (
      <View style={[desktopStyles.wrapper, wrapperHeight]}>
        <Animated.View
          style={[
            desktopStyles.sidebarContainer,
            wrapperHeight,
            sidebarAnimatedStyle,
          ]}
        >
          <AdminSidebar routeName={currentRoute} onNavigate={handleNavigate} />
        </Animated.View>
        <View style={desktopStyles.navigatorContainer}>{navigator}</View>
      </View>
    );
  }

  const isFullscreenRoute = isFullscreenAdminRoute(currentRoute);

  return (
    <GradientBackground blendEdges>
      {!isFullscreenRoute && (
        <AdminHeaderWithMenu
          currentPath={currentPath}
          onNavigate={handleNavigate}
          onLogoPress={handleLogoPress}
          onSignOut={handleSignOut}
        />
      )}
      <View style={mobileStyles.navigatorContainer}>
        <ThemeProvider value={transparentTheme}>{navigator}</ThemeProvider>
      </View>
    </GradientBackground>
  );
};

const mobileStyles = StyleSheet.create({
  navigatorContainer: {
    flex: 1,
  },
});

const desktopStyles = StyleSheet.create({
  wrapper: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#171717',
  },
  sidebarContainer: {
    backgroundColor: SEMANTIC_COLORS.background.secondary,
    flexShrink: 0,
    overflow: 'hidden',
  },
  navigatorContainer: {
    flex: 1,
  },
});
